import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Button, Modal, Form, Alert, Badge, InputGroup } from 'react-bootstrap';
import { Edit2, Trash2, Plus, Search, Mail, UserX, UserCheck } from 'react-feather';
import axios from 'axios';
import { useAuthUser } from 'react-auth-kit';

const UserManagement = () => {
    const authUser = useAuthUser();
    const apiUrl = process.env.REACT_APP_URL;

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    // Form data
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        role: 'employee',
        status: 'active',
        password: '',
        permissions: {
            dashboard: true,
            users: false,
            finances: false,
            maintenance: false,
            reports: false,
            settings: false
        }
    });

    const [companyLimits, setCompanyLimits] = useState({
        max_users: 5,
        current_users: 0
    });

    useEffect(() => {
        fetchUsers();
        fetchCompanyLimits();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${apiUrl}/companies/users`, {
                headers: {
                    'Authorization': `Bearer ${authUser()?.token || localStorage.getItem('_auth')}`
                }
            });
            setUsers(response.data);
        } catch (err) {
            setError('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanyLimits = async () => {
        try {
            const response = await axios.get(`${apiUrl}/companies/limits`, {
                headers: {
                    'Authorization': `Bearer ${authUser()?.token || localStorage.getItem('_auth')}`
                }
            });
            setCompanyLimits(response.data);
        } catch (err) {
            console.error('Failed to load company limits');
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        if (users.length >= companyLimits.max_users) {
            setError('You have reached your subscription limit. Please upgrade to add more users.');
            setLoading(false);
            return;
        }

        try {
            const response = await axios.post(`${apiUrl}/companies/users`, formData, {
                headers: {
                    'Authorization': `Bearer ${authUser()?.token || localStorage.getItem('_auth')}`,
                    'Content-Type': 'application/json'
                }
            });

            setSuccess('User added successfully!');
            setShowAddModal(false);
            resetForm();
            fetchUsers();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to add user');
        } finally {
            setLoading(false);
        }
    };

    const handleEditUser = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            const response = await axios.put(`${apiUrl}/companies/users/${selectedUser.id}`, formData, {
                headers: {
                    'Authorization': `Bearer ${authUser()?.token || localStorage.getItem('_auth')}`,
                    'Content-Type': 'application/json'
                }
            });

            setSuccess('User updated successfully!');
            setShowEditModal(false);
            resetForm();
            fetchUsers();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update user');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = async () => {
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            await axios.delete(`${apiUrl}/companies/users/${selectedUser.id}`, {
                headers: {
                    'Authorization': `Bearer ${authUser()?.token || localStorage.getItem('_auth')}`
                }
            });

            setSuccess('User deleted successfully!');
            setShowDeleteModal(false);
            setSelectedUser(null);
            fetchUsers();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to delete user');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleUserStatus = async (user) => {
        try {
            const newStatus = user.status === 'active' ? 'inactive' : 'active';
            await axios.patch(`${apiUrl}/companies/users/${user.id}/status`,
                { status: newStatus },
                {
                    headers: {
                        'Authorization': `Bearer ${authUser()?.token || localStorage.getItem('_auth')}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            setSuccess(`User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully!`);
            fetchUsers();
        } catch (err) {
            setError('Failed to update user status');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            role: 'employee',
            status: 'active',
            password: '',
            permissions: {
                dashboard: true,
                users: false,
                finances: false,
                maintenance: false,
                reports: false,
                settings: false
            }
        });
        setSelectedUser(null);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name.startsWith('permissions.')) {
            const permissionKey = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                permissions: {
                    ...prev.permissions,
                    [permissionKey]: checked
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const openEditModal = (user) => {
        setSelectedUser(user);
        setFormData({
            name: user.name,
            email: user.email,
            role: user.role,
            status: user.status,
            password: '',
            permissions: user.permissions || {
                dashboard: true,
                users: false,
                finances: false,
                maintenance: false,
                reports: false,
                settings: false
            }
        });
        setShowEditModal(true);
    };

    const openDeleteModal = (user) => {
        setSelectedUser(user);
        setShowDeleteModal(true);
    };

    const filteredUsers = users.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.role.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getRoleBadgeVariant = (role) => {
        switch (role) {
            case 'admin': return 'danger';
            case 'manager': return 'warning';
            case 'employee': return 'primary';
            default: return 'secondary';
        }
    };

    const getStatusBadgeVariant = (status) => {
        return status === 'active' ? 'success' : 'secondary';
    };

    return (
        <div className="main-content">
            <div className="container-fluid">
                <Row>
                    <Col>
                        <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                            <h4 className="mb-sm-0">User Management</h4>
                            <div className="page-title-right">
                                <Badge bg="info" className="me-2">
                                    {users.length}/{companyLimits.max_users} Users
                                </Badge>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => setShowAddModal(true)}
                                    disabled={users.length >= companyLimits.max_users}
                                >
                                    <Plus size={16} className="me-1" />
                                    Add User
                                </Button>
                            </div>
                        </div>
                    </Col>
                </Row>

                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <Row>
                    <Col>
                        <Card>
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <h5 className="card-title mb-0">Company Users</h5>
                                <div className="d-flex align-items-center">
                                    <InputGroup style={{ width: '300px' }}>
                                        <InputGroup.Text>
                                            <Search size={16} />
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search users..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </InputGroup>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {loading ? (
                                    <div className="d-flex justify-content-center p-4">
                                        <div className="spinner-border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <Table responsive hover>
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Email</th>
                                                <th>Role</th>
                                                <th>Status</th>
                                                <th>Last Login</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredUsers.map((user) => (
                                                <tr key={user.id}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <div className="avatar-sm bg-light rounded-circle d-flex align-items-center justify-content-center me-2">
                                                                {user.name.charAt(0).toUpperCase()}
                                                            </div>
                                                            {user.name}
                                                        </div>
                                                    </td>
                                                    <td>{user.email}</td>
                                                    <td>
                                                        <Badge bg={getRoleBadgeVariant(user.role)}>
                                                            {user.role}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Badge bg={getStatusBadgeVariant(user.status)}>
                                                            {user.status}
                                                        </Badge>
                                                    </td>
                                                    <td>{user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}</td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => openEditModal(user)}
                                                                title="Edit User"
                                                            >
                                                                <Edit2 size={14} />
                                                            </Button>
                                                            <Button
                                                                variant={user.status === 'active' ? 'outline-warning' : 'outline-success'}
                                                                size="sm"
                                                                onClick={() => handleToggleUserStatus(user)}
                                                                title={user.status === 'active' ? 'Deactivate User' : 'Activate User'}
                                                            >
                                                                {user.status === 'active' ? <UserX size={14} /> : <UserCheck size={14} />}
                                                            </Button>
                                                            {user.role !== 'admin' && (
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={() => openDeleteModal(user)}
                                                                    title="Delete User"
                                                                >
                                                                    <Trash2 size={14} />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}

                                {!loading && filteredUsers.length === 0 && (
                                    <div className="text-center p-4">
                                        <p className="text-muted">No users found</p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Add User Modal */}
                <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Add New User</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form onSubmit={handleAddUser}>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Full Name *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email *</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Role *</Form.Label>
                                        <Form.Select
                                            name="role"
                                            value={formData.role}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="employee">Employee</option>
                                            <option value="manager">Manager</option>
                                            <option value="admin">Admin</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Password *</Form.Label>
                                        <Form.Control
                                            type="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label>Permissions</Form.Label>
                                <Row>
                                    {Object.entries(formData.permissions).map(([permission, enabled]) => (
                                        <Col md={4} key={permission} className="mb-2">
                                            <Form.Check
                                                type="checkbox"
                                                name={`permissions.${permission}`}
                                                label={permission.charAt(0).toUpperCase() + permission.slice(1)}
                                                checked={enabled}
                                                onChange={handleInputChange}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            </Form.Group>

                            <div className="d-flex justify-content-end">
                                <Button variant="secondary" onClick={() => setShowAddModal(false)} className="me-2">
                                    Cancel
                                </Button>
                                <Button variant="primary" type="submit" disabled={loading}>
                                    {loading ? 'Adding...' : 'Add User'}
                                </Button>
                            </div>
                        </Form>
                    </Modal.Body>
                </Modal>

                {/* Edit User Modal */}
                <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Edit User</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form onSubmit={handleEditUser}>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Full Name *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email *</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Role *</Form.Label>
                                        <Form.Select
                                            name="role"
                                            value={formData.role}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="employee">Employee</option>
                                            <option value="manager">Manager</option>
                                            <option value="admin">Admin</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Status *</Form.Label>
                                        <Form.Select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label>New Password (leave blank to keep current)</Form.Label>
                                <Form.Control
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Permissions</Form.Label>
                                <Row>
                                    {Object.entries(formData.permissions).map(([permission, enabled]) => (
                                        <Col md={4} key={permission} className="mb-2">
                                            <Form.Check
                                                type="checkbox"
                                                name={`permissions.${permission}`}
                                                label={permission.charAt(0).toUpperCase() + permission.slice(1)}
                                                checked={enabled}
                                                onChange={handleInputChange}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            </Form.Group>

                            <div className="d-flex justify-content-end">
                                <Button variant="secondary" onClick={() => setShowEditModal(false)} className="me-2">
                                    Cancel
                                </Button>
                                <Button variant="primary" type="submit" disabled={loading}>
                                    {loading ? 'Updating...' : 'Update User'}
                                </Button>
                            </div>
                        </Form>
                    </Modal.Body>
                </Modal>

                {/* Delete User Modal */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Delete User</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Are you sure you want to delete <strong>{selectedUser?.name}</strong>?</p>
                        <p className="text-danger">This action cannot be undone.</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleDeleteUser} disabled={loading}>
                            {loading ? 'Deleting...' : 'Delete User'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default UserManagement;
